import {
  type JSX,
  Show,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  onMount,
} from "solid-js";

import Icon from "@/components/Icon";
import { useClick } from "@/hooks/useClick.ts";
import { useNotification } from "@/hooks/useNotification.ts";
import { LocaleMap } from "@/i18n/config.ts";
import { getLangKey, useI18n } from "@/i18n/index.ts";
import { formatDate } from "@/lib/dateUtils.ts";
import { isMobile } from "@/lib/isMobile.ts";
import { cn } from "@/lib/tailwindcssTool";
import { renderInnerHtml } from "@/lib/utils";
import { MsgInfo } from "@/types/swagger/notice.ts";

import "./NotificationCard.css";

const { t } = useI18n();

interface NotificationCardProps {
  data: MsgInfo;
  closeCallback?: () => void;
  class?: string;
  style?: Record<string, string>;
}

interface CardItemProps {
  class?: string;
  children: JSX.Element;
  style?: Record<string, string>;
  clickCallback?: () => void;
}

const CardItem = (props: CardItemProps): JSX.Element => {
  return (
    <div
      class={cn("flex justify-between pt-2.5", props.class)}
      style={{ "word-break": "break-word", ...props.style }}
      onClick={props.clickCallback}
    >
      {props.children}
    </div>
  );
};

export default function NotificationCard(props: NotificationCardProps) {
  const notificationStore = useNotification();
  const data = () => props.data;
  let descRef: HTMLParagraphElement | null = null;
  const { click } = useClick();

  const [isExpanded, setIsExpanded] = createSignal(false);
  const [hasOverflow, setHasOverflow] = createSignal(false);
  const [cardDetail, setCardDetail] = createSignal({
    title: "",
    time_unix: 0,
    content: "",
    jump_text: "",
    img: "",
  });

  createEffect(
    on(
      createMemo(() => JSON.stringify(props.data)),
      () => {
        const langInfo = JSON.parse(data()?.lang_info);
        const dataContent = data()?.lang_content;

        /* prettier-ignore */
        const langInfoWithDefault = langInfo?.[getLangKey()] || langInfo?.[LocaleMap.en];

        const getContent = () => {
          // 優先使用 lang_content
          if (dataContent?.[getLangKey()]) {
            return dataContent?.[getLangKey()] || dataContent?.[LocaleMap.en];
          }
          if (langInfo?.[LocaleMap.en]) {
            return dataContent?.[LocaleMap.en];
          }
          // 再使用 lang_info
          if (langInfo?.[getLangKey()]) {
            return langInfo?.[getLangKey()];
          }
          if (langInfo?.[LocaleMap.en]) {
            return langInfo?.[LocaleMap.en];
          }
        };

        setCardDetail({
          content: getContent(),
          ...langInfoWithDefault,
        });
      },
    ),
  );

  const toggleExpand = () => {
    setIsExpanded(!isExpanded());
    // 强制保持按钮显示状态
    if (!hasOverflow()) setHasOverflow(true);
  };

  onMount(() => {
    // 初始检测逻辑
    const initialCheck = () => {
      // @ts-expect-error temp
      const isOverflow = descRef.scrollHeight > descRef.clientHeight;
      setHasOverflow(isOverflow);
    };

    // 延迟执行确保DOM渲染完成
    setTimeout(initialCheck, 50);

    // 保留ResizeObserver但增加展开状态判断
    const observer = new ResizeObserver(() => {
      if (!isExpanded()) {
        // 仅在收起状态检测溢出
        initialCheck();
      }
    });
    // @ts-expect-error temp
    observer.observe(descRef);

    // 清理观察器
    onCleanup(() => observer.disconnect());
  });

  const clickItem = () => {
    if (data().status === 1) return;
    notificationStore.readMsgCallback({
      id: data().id,
      msg_type: data().msg_type,
    });
  };

  const clickLink = () => {
    notificationStore.jumpClickCallback({
      click_id: data().click_id,
      click_params: data().click_params,
      click,
    });
  };

  return (
    <li
      class={cn(
        "bg-general-bg3 inline-block w-full justify-between rounded-[8px] p-[12px]",
        props.class,
      )}
      style={{ ...props.style }}
      // style={{ background: "#363838", ...props.style }}
      onClick={clickItem}
    >
      <CardItem class="pt-0">
        <span
          class={cn(
            "text-general-darkzi flex-1 whitespace-normal break-all pr-2 font-medium",
            isMobile() ? "text-[12px]" : "text-[14px]",
          )}
        >
          {formatDate(data().time_unix, 1)}
        </span>
        <Show when={data().status === 0} fallback={<></>}>
          <span
            style={{
              width: "7px",
              height: "7px",
              "border-radius": "50%",
              "background-color": "var(--general-tips)",
              display: "inline-block",
            }}
          ></span>
        </Show>
      </CardItem>
      <CardItem
        class={cn(
          "font-semibold text-[var(--general-light)]",
          isMobile() ? "text-[13px]" : "text-[15px]",
        )}
      >
        {cardDetail()?.title}
      </CardItem>
      <Show when={cardDetail()?.img} fallback={<></>}>
        <CardItem class="px-[0.5px] pt-2">
          <div
            class={cn(
              "relative w-full rounded-[10px] bg-cover",
              isMobile() ? "h-[calc((100vw-48px)/2.42)]" : "h-[145px]",
            )}
            style={{
              "background-image": `url(${cardDetail()?.img})`,
            }}
          ></div>
        </CardItem>
      </Show>
      <CardItem
        class={`desc-box cursor-pointer ${cardDetail()?.content ? (cardDetail()?.img ? "pt-1.5" : "pt-2") : "pt-0"}`}
        clickCallback={() => {
          if (hasOverflow()) toggleExpand();
        }}
      >
        <div
          ref={(el) => (descRef = el)}
          classList={{ expanded: isExpanded() }}
          class={cn(
            "desc text-general-bright break-normal font-medium leading-[150%]",
            isMobile() ? "text-[12px]" : "text-[14px]",
          )}
        >
          <div innerHTML={renderInnerHtml(cardDetail()?.content)} />
        </div>
      </CardItem>
      <Show when={hasOverflow()} fallback={<></>}>
        <div
          class="flex cursor-pointer justify-end"
          style={{ "align-items": "center", color: "var(--general-light)" }}
          onClick={toggleExpand}
        >
          <Show
            when={isExpanded()}
            fallback={
              <div
                class={cn(
                  "mt-1.5 pr-[4px] text-[14px]",
                  isMobile() ? "text-[12px]" : "text-[14px]",
                )}
              >
                {t("home.show_all")}
              </div>
            }
          >
            <div
              class={cn("pr-[4px]", isMobile() ? "text-[12px]" : "text-[14px]")}
            >
              {t("notice.hide")}
            </div>
          </Show>
          <span
            class={`mt-1.5 shrink-0 transition-transform duration-200 ${
              isExpanded() ? "mb-2 rotate-180" : ""
            }`}
          >
            <Icon
              name="general/arrow_down"
              class="text-general-light size-[10px]"
            />
          </span>
        </div>
      </Show>
      <Show when={cardDetail()?.jump_text} fallback={<></>}>
        <div
          class={cn(
            "mt-3 inline-flex w-full cursor-pointer justify-center font-medium",
            isMobile() ? "text-[12px]" : "text-[14px]",
          )}
          style={{
            "align-items": "center",
            color: "var(--color-general-main)",
            "word-break": "break-word",
          }}
          onClick={clickLink}
        >
          <span class="pr-2">{cardDetail()?.jump_text}</span>
          <Icon
            name="general/arrow_right"
            class="text-general-main size-[12px]"
          />
        </div>
      </Show>
    </li>
  );
}
